<template>
	<div class="quote-detail">
		<div class="card-item" v-for="(item, index) in formattedMainData" :key="index">
			<p>{{ item.value }}{{ item.unit }}</p>
			<p>{{ item.label }}</p>
		</div>
		<div class="right-tip" v-if="formattedExpandData && formattedExpandData.length > 0">
			<el-popover popper-class="quote-popper" v-model="visible" placement="bottom-end">
				<template #reference>
					<SvgIcons name="down" class="down-icon" :class="[visible ? 'animate' : '']" />
				</template>

				<template #default>
					<div class="new-layout">
						<div class="card-item" v-for="(item, index) in formattedExpandData" :key="index">
							<p>{{ item.value }}{{ item.unit }}</p>
							<p>{{ item.label }}</p>
						</div>
					</div>
				</template>
			</el-popover>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { convertNumberToCNUnit } from '@/utils/api';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import i18n, { ZH, EN } from '@/locales';

const props = defineProps({
	stockQuoteData: {
		type: Object,
		default: () => ({})
	},
	isPeTtm: {
		type: Boolean,
		default: false
	},
	isOtherQuote: {
		type: Boolean,
		default: false
	}
});

const { proxy } = getCurrentInstance();
const { isHKStock, isUSStock } = useStockVO();
const visible = ref(false);

const propsMap = {
	pb: proxy.$t('enterprise.P/B'),
	peTtm: props.isPeTtm ? proxy.$t(`enterprise.clear-P/E`) : proxy.$t('enterprise.P/E'),
	highPrice: proxy.$t('enterprise.high'),
	lowPrice: proxy.$t('enterprise.low'),

	// 港股区分
	turnoverVolumeShares: proxy.$t('enterprise.volume'),
	turnoverVolume: proxy.$t('enterprise.volume'),

	turnoverValue: proxy.$t('enterprise.turnover'),
	negotiableMv: proxy.$t('enterprise.float-cap'),
	totalMv: proxy.$t('enterprise.market-cap')
};

const formatData = result => {
	const createRow = props => {
		return props.map(prop => ({
			label: propsMap[prop],
			prop: prop
		}));
	};

	let rowArray = [];

	if (props.isOtherQuote) {
		const row1Props = ['highPrice', 'lowPrice'];
		const row2Props = ['turnoverVolume', 'turnoverValue', 'negotiableMv', 'totalMv'];
		rowArray = [createRow(row1Props), createRow(row2Props)];
	} else {
		const row1Props = isUSStock.value ? ['peTtm'] : ['pb', 'peTtm'];
		const row2Props = isUSStock.value
			? ['highPrice', 'lowPrice', 'turnoverVolumeShares', 'totalMv']
			: [
					'highPrice',
					'lowPrice',
					isHKStock.value ? 'turnoverVolumeShares' : 'turnoverVolume',
					'turnoverValue',
					'negotiableMv',
					'totalMv'
			  ];
		rowArray = [createRow(row1Props), createRow(row2Props)];
	}

	const formatValueFn = (propName, value) => {
		if (propName == 'changePct') {
			return `${value}%`;
		}
		return value;
	};
	const formatUnitFn = (propName, unit) => {
		if (propName === 'turnoverVolume') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}手`;
		}
		if (propName == 'turnoverVolumeShares') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}股`;
		}
		return unit;
	};
	const formatUnitStyleFn = (propName, value) => {
		if (propName == 'change' || propName == 'changePct') {
			return value > 0 ? 'up' : 'down';
		}
		return null;
	};

	return rowArray.map(item => {
		return item.map(childItem => {
			// 中文时可能会使用万手，切换至英文时需要将手置换为股 - A股 一手固定100股
			if (proxy.$i18n.locale === EN && childItem.prop === 'turnoverVolume') {
				result[childItem.prop] *= 100; // 将 turnoverVolume  单位由手转换为股
			}

			const { value, unit } = convertNumberToCNUnit(result[childItem.prop]);

			return {
				label: childItem.label,
				value: formatValueFn(childItem.prop, value),
				unit: formatUnitFn(childItem.prop, unit),
				unitStyle: formatUnitStyleFn(childItem.prop, value)
			};
		});
	});
};

const formattedData = computed(() => {
	if (!props.stockQuoteData || Object.keys(props.stockQuoteData).length === 0) {
		return [[], []];
	}
	return formatData(props.stockQuoteData);
});

const formattedMainData = computed(() => formattedData.value[0] || []);
const formattedExpandData = computed(() => formattedData.value[1] || []);
</script>

<style lang="less">
.quote-popper {
	&.el-popover {
		padding: 24px 40px;
		.popper__arrow {
			display: none;
		}
	}
}
</style>

<style lang="less" scoped>
.quote-detail {
	display: flex;
	.card-item {
		margin-right: 40px;
	}

	.card-item:nth-child(2) {
		margin-right: 16px;
	}
	.right-tip {
		margin: auto 0;
		background-color: var(--fill-normal-color, #f2f3f5);
		.down-icon {
			outline: none;
			cursor: pointer;
			font-size: 24px;
			transition: transform 0.3s;
			transform-origin: center;
			color: #4e5969 !important;
		}
	}
	.animate {
		transform: rotate(-180deg);
	}
}

.new-layout {
	display: grid;
	grid-template-columns: repeat(2, auto);
	gap: 24px 40px;
	.card-item:nth-child(2n + 1) {
		width: auto;
	}
}

.card-item {
	text-align: right;
	width: 82px;
	p {
		font-size: 14px;

		font-weight: 400;

		&:nth-child(1) {
			color: var(--t-emphasis-color, #1d2129);
			margin-bottom: 8px;
		}

		&:nth-child(2) {
			color: var(--t-secondary-info-color, #86909c);
		}
	}
}
</style>
